import { defineStore } from 'pinia';
import {
    LotteryLobbyList,
    LotteryExpectType,
    WapLotteryLobbyList,
    GameListPc,
    LotteryExpectPlay,
    FluffyPlay,
    BetType,
    BetItem,
    CombType,
    ChoseItemType,
    LotteryAdsListType,
} from 'types/wsLotteryType';
import { findCurrentExpect, findPcSmallCurrentExpect, findSmallCurrentExpect, findZodiac, getExpectList, getFindBetTotal, getFindChangLong, getLotteryAdsList } from 'service/wsLottery';
import { useSocketStore } from 'store/modules/socketStore';
import HiCache from 'utils/cache';
import HiStance from 'utils/HiStance';
import { ChangLongItemType, ChangLongOddsType, OddsItemType } from 'types/lottery';

export interface WsLotteryState {
    // 彩票大厅数据
    lotteryLobbyList: LotteryLobbyList[];
    // H5大厅数据
    wapLotteryLobbyList: WapLotteryLobbyList[];
    // 侧边栏彩种分类
    categoryFindAll: WapLotteryLobbyList[];
    // 玩法列表
    playList: LotteryExpectType;
    // PC侧边点击项
    pcSideActiveCode: number;

    gameCode: number;
    playCodeList: number[];
    playCodeItemList: FluffyPlay[] | CombType[];
    playCodeItemCombList: CombType[];
    account: String;
    betItem: BetItem[];
    choseItem: ChoseItemType[];

    // ==== PC投注数据 ====
    isShowStoke: boolean; // 是否显示折线图, 默认显
    isShowMissing: boolean;
    movementsCompData: any;
    pcActiveTitle: string;
    quickMoneyInput: string;
    checkList: any[];
    zodiacList: { [key: string]: number[] };
    natalSign: string; // 本命肖
    pcSelectList: (FluffyPlay | CombType)[]; // 点击投注 之后的显示
    pcIsTimeClose: boolean;

    bettingGameCode: number;

    // 投注确认弹窗
    bettingConfirm: boolean;

    // 追号确认弹窗
    chaseConfirm: boolean;

    [key: string]: any;

    pcTrendItems: any;
    lotteryAdsList: LotteryAdsListType[];
    isShowGuidance: boolean;
    changLongList: ChangLongItemType[];
    isDisabledMusic: boolean;
}

export const useWsLotteryStore = defineStore('wsLottery', {
    state: (): WsLotteryState => {
        return {
            // 彩票大厅数据
            lotteryLobbyList: [],
            // H5大厅数据
            wapLotteryLobbyList: [],
            // 侧边栏彩种分类
            categoryFindAll: [],
            // 玩法列表
            playList: {} as LotteryExpectType,
            // PC侧边点击项, 0:全部 -1:热门 -2收藏
            pcSideActiveCode: 0,

            gameCode: 0,
            playCodeList: [],
            playCodeItemList: [],
            playCodeItemCombList: [],
            account: '',
            betItem: [],
            choseItem: [],
            // 是否显示折线图
            isShowStoke: true,
            // 遗漏显示
            isShowMissing: true,
            // movements data
            movementsCompData: {},
            pcActiveTitle: '',
            quickMoneyInput: '',
            checkList: [],
            zodiacList: {},
            natalSign: '',
            pcSelectList: [],
            pcTrendItems: {},
            pcIsTimeClose: false,
            // 投注确认弹窗
            bettingConfirm: JSON.parse(<string>HiCache.getCache('bettingConfirm') || 'true') ?? true,
            // 追号确认弹窗
            chaseConfirm: JSON.parse(<string>HiCache.getCache('chaseConfirm') || 'true') ?? true,
            lotteryAdsList: [],
            isShowGuidance: true,

            bettingGameCode: 0,
            changLongList: [],
            isDisabledMusic: JSON.parse(<string>HiCache.getCache('disabledMusic') || 'false') ?? false,
        };
    },
    getters: {
        getCategoryFindOriginal(state: WsLotteryState) {
            return state.categoryFindAll;
        },
        getDisabledMusic(state: WsLotteryState) {
            return state.isDisabledMusic;
        },
        getChangLongList(state: WsLotteryState) {
            return state.changLongList;
        },
        getPcTrendItems(state: WsLotteryState) {
            return state.pcTrendItems;
        },
        getLotteryLobbyList(state: WsLotteryState) {
            return state.lotteryLobbyList;
        },
        getCategoryFindAll(state: WsLotteryState) {
            return state.categoryFindAll;
        },
        getWapLotteryLobbyList(state: WsLotteryState) {
            return state.wapLotteryLobbyList;
        },
        getPlayList(state: WsLotteryState) {
            return state.playList;
        },
        getPcSideActiveCode(state: WsLotteryState) {
            return state.pcSideActiveCode;
        },
        getPcUpdateHallData(state: WsLotteryState) {
            const list = this.getCategoryFindAll?.map(item => item.gameList).flat();

            return (code: number): LotteryLobbyList[] => {
                if (code === 0) {
                    // 全部
                    return this.getLotteryLobbyList;
                } else if (code === 1111) {
                    // 热门
                    const hotCodes = list?.filter(item => item.isHot)?.map(item => item.gameCode);
                    return this.getLotteryLobbyList.filter(item => hotCodes.includes(item.gameCode));
                } else if (code === 2222) {
                    // 收藏
                    return this.getLotteryLobbyList.filter(item => item.isFavorite);
                } else {
                    return this.getLotteryLobbyList.filter(item => item.categoryCode === code);
                }
            };
        },
        getPlayCodeList(state: WsLotteryState) {
            return state.playCodeList;
        },
        getPlayCodeItemList(state: WsLotteryState) {
            return state.playCodeItemList;
        },
        getPlayCodeItemCombList(state: WsLotteryState) {
            return state.playCodeItemCombList;
        },
        getBetMoney(state: WsLotteryState) {
            return state.account;
        },
        getBetItem(state: WsLotteryState) {
            return state.betItem;
        },
        getChoseItem(state: WsLotteryState) {
            return state.choseItem;
        },
        getPcActiveTitle(state) {
            return state.pcActiveTitle;
        },
        getCurrentGameCode(state) {
            return state.gameCode;
        },
        LotteryAdsList(state) {
            return state.lotteryAdsList;
        },
        IsShowGuidance(state) {
            return state.isShowGuidance;
        },
    },
    actions: {
        // 禁用音效
        setDisabledMusic(bool: boolean) {
            HiCache.setCache('disabledMusic', bool);
            this.isDisabledMusic = bool;
        },
        setLotteryLobbyList(list: LotteryLobbyList[]) {
            this.lotteryLobbyList = list;
        },
        setCategoryFindAll(list: WapLotteryLobbyList[]) {
            this.categoryFindAll = list;
        },
        setWapLotteryLobbyList(list: WapLotteryLobbyList[]) {
            this.wapLotteryLobbyList = list;
        },
        setIsShowGuidance(data: boolean) {
            this.isShowGuidance = data;
        },
        async asyncGetPlayList(gameCode: number) {
            const res = await findCurrentExpect(gameCode);
            const { code, data, msg } = res;
            if (code === 0) {
                console.log(gameCode + '玩法', data);
                this.setPlayList(data);
            }
            return data;
        },
        async asyncGetChangLongList() {
            const res = await getFindChangLong();
            const { code, data, msg } = res;
            if (code === 0) {
                this.changLongList = this.CreateChangLongList(data);
            }
            return data;
        },

        CreateChangLongList(data: any[]) {
            return data?.map(item => {
                return {
                    categoryCode: item[0],
                    categoryName: item[1],
                    gameCode: item[2],
                    gameName: item[3],
                    expect: item[4],
                    startTime: item[5],
                    stopTime: item[6],
                    endTime: item[7],
                    stopSecond: item[8],
                    endSecond: item[9],
                    smallIcon: item[10],
                    bigIcon: item[11],
                    h5Icon: item[12],
                    playCode: item[13],
                    content: item[14],
                    drawCount: item[15],
                    play: this.GetChangLongOdds(item[16]),
                    differTime: (isNaN(item[9]) || item[9] < 0 ? 0 : item[9]) - (isNaN(item[8]) || item[8] < 0 ? 0 : item[8]),
                };
            });
        },

        GetChangLongOdds(data: ChangLongOddsType[]) {
            return data?.map(item => ({
                playCode: item[0],
                result: item[1],
                odds: item[2],
                title: item[3],
                betContentCodes: item[4],
            }));
        },
        async asyncGetPlayListPC(gameCode: number) {
            const res = await findPcSmallCurrentExpect(gameCode);
            const { code, data, msg } = res;
            if (code === 0) {
                console.log(gameCode + '玩法', data);
                this.setPlayList(data);
            }
            return data;
        },
        async asyncGetExpectList(params: any) {
            const res = await getExpectList(params);
            const { code, data, msg } = res;
            return data;
        },
        async asyncGetZodiac() {
            const res = await findZodiac();
            const { data, msg } = res;
            this.zodiacList = data;
            this.natalSign = msg;
            return res;
        },
        // 获取彩票首页轮播图首页
        async asyncGetLotteryAdsList() {
            if (this.lotteryAdsList?.length > 0) {
                return this.lotteryAdsList;
            } else {
                const res = await getLotteryAdsList();
                const { data, msg } = res;
                this.lotteryAdsList = data;
                return data;
            }
        },
        // 获取投注未结算和输赢
        async asyncGetFindBetTotal(data = {}) {
            return await getFindBetTotal(data);
        },

        // 处理playList 单独抽离
        setPlayList(data: LotteryExpectType) {
            this.playList = data;
            this.playList.differTime = (isNaN(data?.endSecond) || data?.endSecond < 0 ? 0 : data?.endSecond) - (isNaN(data?.stopSecond) || data?.stopSecond < 0 ? 0 : data?.stopSecond);
        },

        setPcSideActiveCode(code: number) {
            this.pcSideActiveCode = code;
        },
        setFavoriteStatus(code: number) {
            const index = this.lotteryLobbyList.findIndex(item => item.gameCode === code);
            if (index !== -1) {
                this.lotteryLobbyList[index].isFavorite = !this.lotteryLobbyList[index].isFavorite;
            }

            const favoriteData = this.lotteryLobbyList.filter(item => item.isFavorite);
            if (favoriteData.length === 0 && this.pcSideActiveCode === 2222) {
                this.pcSideActiveCode = 0;
            }
        },
        setGameCode(num: number) {
            this.gameCode = num;
        },
        setPlayCodeList(arr: number[]) {
            this.playCodeList = arr;
        },
        setPlayCodeItemList(arr: FluffyPlay[] | CombType[]) {
            this.playCodeItemList = arr;
        },
        submitBet(data: BetType) {
            const socketStore = useSocketStore();
            const ws = socketStore.getWebSocket;
            ws?.sendMessage(data);
            console.log('执行submitBet---');
        },
        setBetMoney(data: string) {
            this.account = data;
        },
        setItemBetMoney(data: string | number, itemPlayCode?: number) {
            if (itemPlayCode) {
                this.playCodeItemList = this.playCodeItemList?.map(item => {
                    if (item?.playCode === itemPlayCode) {
                        return {
                            ...item,
                            money: data,
                        };
                    }
                    return item;
                });
            } else {
                this.playCodeItemList = this.playCodeItemList?.map(item => {
                    return {
                        ...item,
                        money: data,
                    };
                });
            }
        },
        setSubmitBet(type: string, isPc = false) {
            // 投注处理
            if (!isPc) {
                this.betItem = this.playCodeItemList?.map(item => {
                    return {
                        expect: this.playList?.expect,
                        content: item.result,
                        contentTitle: item.title,
                        money: item.isDouble ? Number((item?.money || 0) * 2) : Number(item?.money || 0),
                        odds: item.odds,
                        playCode: item.playCode,
                        betMultiple: item.isDouble ? 2 : 1,
                        singleBetMoney: Number(item?.money || 0),
                    };
                });
            } else {
                this.betItem = this.pcSelectList?.map(item => {
                    return {
                        expect: this.playList?.expect,
                        content: item.result,
                        contentTitle: item.title,
                        money: item.isDouble ? Number(item?.money || 0) * 2 : Number(item?.money || 0),
                        odds: item.odds,
                        playCode: item.playCode,
                        betMultiple: item.isDouble ? 2 : 1,
                        singleBetMoney: Number(item?.money || 0),
                    };
                });
            }
        },
        setSubmitBet2(
            value?: any,
            otherInfo?: {
                total?: string | number;
                money?: string | number;
                betMultiple?: number;
                type?: string;
            },
            isPc = false
        ) {
            if (otherInfo?.type === 'default') {
                // 投注处理
                if (!isPc) {
                    this.betItem = this.playCodeItemList?.map(item => {
                        return {
                            expect: this.playList?.expect,
                            content: item.result,
                            contentTitle: item.title,
                            money: Number(otherInfo?.total || 0),
                            odds: item.odds,
                            playCode: item.playCode,
                            betMultiple: otherInfo?.betMultiple || 1,
                            singleBetMoney: Number(otherInfo?.money || 0),
                        };
                    });
                } else {
                    this.betItem = this.pcSelectList?.map(item => {
                        return {
                            expect: this.playList?.expect,
                            content: item.result,
                            contentTitle: item.title,
                            money: Number(otherInfo?.total || 0),
                            odds: item.odds,
                            playCode: item.playCode,
                            betMultiple: otherInfo?.betMultiple || 1,
                            singleBetMoney: Number(otherInfo?.money || 0),
                        };
                    });
                }
            } else {
                // 投注处理
                if (!isPc) {
                    this.betItem = [
                        {
                            expect: otherInfo?.type === 'changLong' ? value?.expect : this.playList?.expect,
                            content: value.result,
                            contentTitle: value.title,
                            money: Number(value?.money || 0),
                            odds: value.odds,
                            playCode: value.playCode,
                            betMultiple: value.betMultiple,
                            singleBetMoney: Number(value?.singleBetMoney || 0),
                        },
                    ];
                } else {
                    this.betItem = [
                        {
                            expect: otherInfo?.type === 'changLong' ? value?.expect : this.playList?.expect,
                            content: value.result,
                            contentTitle: value.title,
                            money: Number(value?.money || 0),
                            odds: value.odds,
                            playCode: value.playCode,
                            betMultiple: value.betMultiple,
                            singleBetMoney: Number(value?.singleBetMoney || 0),
                        },
                    ];
                }
            }
        },
        SET_PC_ACTIVE_TITLE(title: string) {
            this.pcActiveTitle = title;
        },
        SET_PC_QUICK_MONEY_INPUT(money: string) {
            const intValue = Math.floor(parseFloat(money));
            if (isNaN(intValue)) {
                money = '';
            } else {
                money = intValue.toString();
            }
            this.quickMoneyInput = money;
        },
        SET_PC_TREND_ITEMS(item: Object) {
            this.pcTrendItems = item;
        },
        // 投注确认弹窗
        setBettingConfirm(bool: boolean) {
            HiCache.setCache('bettingConfirm', bool);
            this.bettingConfirm = bool;
        },

        // 追号确认弹窗
        setChaseConfirm(bool: boolean) {
            HiCache.setCache('chaseConfirm', bool);
            this.chaseConfirm = bool;
        },

        // PC是否下一步pcSelectList
        setPcSelectList(item: FluffyPlay | CombType, list?: FluffyPlay[] | CombType[]) {
            if (list) {
                this.pcSelectList = list;
            } else {
                this.pcSelectList.push(item);
            }
        },
        clearPcSelectList() {
            this.pcSelectList = [];
        },

        // pc 是否处于封盘
        setPcIsTimeClose(bool: boolean) {
            this.pcIsTimeClose = bool;
        },
    },
});
